import React, { useEffect, useRef, useState } from "react";
import { X, Camera, AlertCircle } from "lucide-react";

// Full-screen camera capture for phones with a receipt framing guide.
export function CameraCapture({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    let stream;
    setErr(""); setReady(false);
    (async () => {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        setErr("Kamera hanya bisa diakses lewat koneksi aman (HTTPS). Gunakan tombol pilih foto sebagai gantinya.");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } }, audio: false,
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
          setReady(true);
        }
      } catch (e) {
        setErr("Kamera tidak dapat diakses. Izinkan akses kamera atau gunakan pilih foto.");
      }
    })();
    return () => { if (stream) stream.getTracks().forEach((t) => t.stop()); };
  }, [open]);

  const shoot = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `nota-${Date.now()}.jpg`, { type: "image/jpeg" });
      onCapture(file);
    }, "image/jpeg", 0.92);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black flex flex-col" data-testid="camera-modal">
      <div className="flex items-center justify-between p-4 text-white">
        <span className="text-sm font-medium">Foto Nota</span>
        <button onClick={onClose} data-testid="camera-close" className="p-1"><X className="w-6 h-6" /></button>
      </div>

      <div className="flex-1 relative overflow-hidden">
        {err ? (
          <div className="h-full flex flex-col items-center justify-center text-white/80 px-8 text-center gap-3">
            <AlertCircle className="w-8 h-8" />
            <p className="text-sm">{err}</p>
          </div>
        ) : (
          <>
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="rounded-lg border-2 border-white/90 shadow-[0_0_0_2000px_rgba(0,0,0,0.35)]" style={{ width: "84%", height: "66%" }}>
                <span className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-white rounded-tl-lg" />
                <span className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-white rounded-tr-lg" />
                <span className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-white rounded-bl-lg" />
                <span className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-white rounded-br-lg" />
              </div>
            </div>
            <p className="absolute bottom-6 inset-x-0 text-center text-white/85 text-xs px-8">
              Posisikan nota di dalam bingkai. Pastikan seluruh teks terbaca, terang, dan tidak buram.
            </p>
          </>
        )}
      </div>

      <div className="p-6 flex items-center justify-center bg-black">
        <button onClick={shoot} disabled={!!err || !ready} data-testid="camera-shoot"
          className="w-[68px] h-[68px] rounded-full bg-white ring-4 ring-white/30 active:scale-95 transition-transform disabled:opacity-40 flex items-center justify-center">
          <Camera className="w-6 h-6 text-black" />
        </button>
      </div>
    </div>
  );
}
